import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ProjectsProps, UserSelectType } from './Projects.model';
import {
  useColumnSettingsApi,
  useEnumerationsApi,
  useFollowUpsApi,
  useProjectsApi,
  useUsersApi
} from '../../service/Api.service';
import { MRT_ColumnDef, MRT_VisibilityState } from 'material-react-table';
import { useSearchParams } from 'react-router-dom';
import {
  deserializeProjectsTableState,
  getProjectExportParameter,
  getProjectsTableFiltersParameter,
  getProjectsTablePaginationParameter,
  PROJECTS_TABLE_STATE_PARAM,
  serializeProjectsTableState
} from '../../core/util/Util';
import MaterialTable, {
  DEFAULT_COLUMN_VISIBILITY
} from '../../components/MaterialTable/MaterialTable';
import { TableChangesProps, TableData } from '../../components/MaterialTable/MaterialTable.model';
import { getProjectsColumns } from './ProjectsColumns';
import { ProjectsOverview } from '../../components/ProjectsOverview/ProjectsOverview';
// eslint-disable-next-line @typescript-eslint/no-redeclare
import { Space, Text } from '@mantine/core';
import { AuthContext } from '../../core/auth/AuthContextProvider';
import {
  EnumerationDto,
  FollowUpsDto,
  RepeatExportRequestDtoExportTypeEnum
} from '../../build/generated-sources/openapi';
import handleErrors from '../../core/error-handler/ErrorHandler';
import { modals } from '@mantine/modals';
import { ExportMode } from '../../components/MaterialTable/ExportMenu';
import { DatabaseLock } from '../../components/DatabaseLock/DatabaseLock';
import { useDatabaseLock } from '../../core/database-lock/DatabaseLockProvider';

const defaultProjectTableChanges = {
  pagination: {
    pageIndex: 0,
    pageSize: 10
  },
  sorting: [],
  columnFilters: [],
  globalFilter: ''
};

export const ProjectsPage = ({ filterMyProjects }: ProjectsProps) => {
  const authContext = useContext(AuthContext);
  const { canModify } = useDatabaseLock();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [usersList, setUsersList] = useState<UserSelectType[]>([]);
  const [projectsTableData, setProjectsTableData] = useState<TableData>({
    data: [],
    totalPages: 1,
    totalElements: 0
  });
  // [DAP-016 / P1] Stav tabulky (filtry, řazení, stránka) se drží POUZE v URL (query param `stav`).
  // Návrat z karty i zkopírovaná URL obnoví stejný stav. Perzistence do sessionStorage byla
  // záměrně odstraněna (P1) — nechceme do budoucna řešit riziko „rozbité" storage. Bez `stav`
  // v URL se použije výchozí stav.
  const [projectTableChanges, setProjectTableChanges] = useState<TableChangesProps>(() => {
    return (
      deserializeProjectsTableState(searchParams.get(PROJECTS_TABLE_STATE_PARAM)) ??
      defaultProjectTableChanges
    );
  });
  const [enumerations, setEnumerations] = useState<EnumerationDto[]>([]);
  const [followUps, setFollowUps] = useState<FollowUpsDto>({});
  const [hasExportSettings, setHasExportSettings] = useState<boolean>(false);
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState | undefined>(
    undefined
  );
  // [DAP-019] Ruční pořadí sloupců (per-user). undefined = zatím nenačteno / bez uloženého pořadí.
  const [columnOrder, setColumnOrder] = useState<string[] | undefined>(undefined);
  const [columnSettingsLoaded, setColumnSettingsLoaded] = useState<boolean>(false);
  // [DAP-019] Guard proti load-race: dokud neproběhl úvodní GET nastavení sloupců,
  // nesmíme nic ukládat — jinak by se dosud nenačtená (výchozí) hodnota poslala na
  // backend a přepsala uloženou. Ref (ne state) použit záměrně, aby byl aktuální
  // synchronně, bez čekání na re-render. Nastaví se i při chybě GETu, aby ukládání
  // nezůstalo trvale zablokované.
  const settingsLoadedRef = useRef<boolean>(false);

  // Zapíše stav tabulky do URL query (DAP-016). Používá replace, aby každá změna filtru
  // / stránky nevytvářela nový záznam v historii prohlížeče — jedno „zpět" z karty tak
  // vede zpět na seznam, ne na mezistav filtrování.
  const syncStateToUrl = useCallback(
    (tableChanges: TableChangesProps) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const serialized = serializeProjectsTableState(tableChanges);
          if (serialized) {
            next.set(PROJECTS_TABLE_STATE_PARAM, serialized);
          } else {
            next.delete(PROJECTS_TABLE_STATE_PARAM);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Aktualizuje stav tabulky a propíše ho do URL (jediné úložiště — bez sessionStorage, viz P1).
  const updateTableChanges = useCallback(
    (tableChanges: TableChangesProps) => {
      setProjectTableChanges(tableChanges);
      syncStateToUrl(tableChanges);
    },
    [syncStateToUrl]
  );

  const useGetProjects = useCallback(async () => {
    const filtersDto = getProjectsTableFiltersParameter(projectTableChanges, filterMyProjects);
    const pagingDto = getProjectsTablePaginationParameter(projectTableChanges);
    return useProjectsApi().getProjects(filtersDto, pagingDto);
  }, [filterMyProjects, projectTableChanges]);

  function callGetProjectsData() {
    useGetProjects()
      .then((result) => {
        const totalPages = result?.data?.totalPages ?? 0;
        setProjectsTableData({
          data: result?.data?.data ?? [],
          totalElements: result?.data?.totalElements ?? 0,
          totalPages: totalPages
        });
        setLoading(false);

        // Ošetření page-overflow (DAP-016): uložená stránka může být mimo rozsah
        // výsledků (např. jiný počet projektů / silnější filtr). V takovém případě
        // spadneme na poslední validní stránku místo prázdné/chybové tabulky.
        const { pageIndex } = projectTableChanges.pagination;
        if (totalPages > 0 && pageIndex > totalPages - 1) {
          updateTableChanges({
            ...projectTableChanges,
            pagination: {
              ...projectTableChanges.pagination,
              pageIndex: totalPages - 1
            }
          });
        }
      })
      .catch((error) => {
        handleErrors(error, 'Při načítání projektů došlo k problému', false);
      });
  }

  function loadExportSettings() {
    useProjectsApi()
      .getSavedExportSettings()
      .then((result) => {
        setHasExportSettings(result?.data && result.data.length > 0);
      })
      .catch((error) => {
        handleErrors(error, 'Při načítání nastavení exportu došlo k problému', false);
      });
  }

  const TABLE_ID = 'projects';

  function loadColumnSettings() {
    useColumnSettingsApi()
      .getColumnSettings(TABLE_ID)
      .then((result) => {
        if (result.status === 200 && result.data?.columns) {
          // Saved columns contain IDs of HIDDEN columns (those set to false)
          // We only need to track columns that differ from MRT's default (which is visible)
          const hiddenColumns = new Set(result.data.columns);
          const visibility: MRT_VisibilityState = {};

          // Set hidden columns to false
          hiddenColumns.forEach((columnId) => {
            visibility[columnId] = false;
          });

          setColumnVisibility(visibility);
        } else {
          // No saved settings, use default
          setColumnVisibility(DEFAULT_COLUMN_VISIBILITY);
        }
        // [DAP-019] Uložené ruční pořadí sloupců (může chybět u starších záznamů).
        if (result.status === 200 && result.data?.columnOrder?.length) {
          setColumnOrder(result.data.columnOrder);
        } else {
          setColumnOrder(undefined);
        }
        settingsLoadedRef.current = true;
        setColumnSettingsLoaded(true);
      })
      .catch((error) => {
        // On error, use default visibility
        setColumnVisibility(DEFAULT_COLUMN_VISIBILITY);
        setColumnOrder(undefined);
        // I při chybě odblokujeme ukládání — jinak by uživatel po neúspěšném GETu
        // nemohl vůbec uložit změnu viditelnosti/pořadí sloupců.
        settingsLoadedRef.current = true;
        setColumnSettingsLoaded(true);
        handleErrors(error, 'Při načítání nastavení sloupců došlo k problému', false);
      });
  }

  // [DAP-019] Skryté sloupce jsou ta ID, která mají viditelnost = false. Ukládáme
  // pouze výjimky z výchozí (viditelné) hodnoty — je to efektivnější.
  function toHiddenColumns(visibility: MRT_VisibilityState): string[] {
    return Object.entries(visibility)
      .filter(([, isVisible]) => !isVisible)
      .map(([columnId]) => columnId);
  }

  // [DAP-019] Viditelnost i pořadí sloupců žijí ve stejném ColumnSettings záznamu,
  // proto při jakékoli změně posíláme oba údaje najednou, aby jeden nepřepsal druhý.
  function persistColumnSettings(visibility: MRT_VisibilityState, order: string[] | undefined) {
    // Neukládáme, dokud neproběhl úvodní load — viz settingsLoadedRef výše. Změna, kterou
    // uživatel provede až po dokončení loadu, se uloží normálně.
    if (!settingsLoadedRef.current) {
      return;
    }
    useColumnSettingsApi()
      .saveColumnSettings(TABLE_ID, { columns: toHiddenColumns(visibility), columnOrder: order })
      .catch((error) => {
        handleErrors(error, 'Při ukládání nastavení sloupců došlo k problému', false);
      });
  }

  const handleColumnVisibilityChange = (visibility: MRT_VisibilityState) => {
    setColumnVisibility(visibility);
    persistColumnSettings(visibility, columnOrder);
  };

  // [DAP-019] Změna ručního pořadí sloupců — uložíme per-user spolu s viditelností.
  const handleColumnOrderChange = (order: string[]) => {
    setColumnOrder(order);
    persistColumnSettings(columnVisibility ?? DEFAULT_COLUMN_VISIBILITY, order);
  };

  useEffect(() => {
    setLoading(true);
    callGetProjectsData();
  }, [projectTableChanges, filterMyProjects]);

  function canSeeUsers() {
    return authContext.hasRole(['user', 'superuser', 'manager', 'sysadmin']);
  }

  useEffect(() => {
    if (canSeeUsers()) {
      useUsersApi()
        .getUsers()
        .then((result) => {
          const usersSelectData: UserSelectType[] = [];
          result?.data?.forEach((keycloakUser) => {
            usersSelectData.push({
              label: keycloakUser.firstName + ' ' + keycloakUser.lastName,
              value: keycloakUser.keycloakId ?? ''
            });
          });
          setUsersList(usersSelectData);
        })
        .catch((error) => {
          handleErrors(error, 'Chyba při načítání uživatelů', false);
        });
    }
  }, []);

  useEffect(() => {
    useEnumerationsApi()
      .getEnumerations()
      .then((result) => {
        setEnumerations(result.data);
      })
      .catch((error) => {
        handleErrors(error, 'Chyba při načítání číselniků', false);
      });
    useFollowUpsApi()
      .getFollowUps()
      .then((result) => {
        setFollowUps(result.data);
      })
      .catch((error) => {
        handleErrors(error, 'Chyba při načítání dat pro návaznost PR KK a SRR ČR', false);
      });
    loadExportSettings();
    loadColumnSettings();
  }, []);

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => getProjectsColumns(usersList, canSeeUsers(), enumerations, followUps),
    [usersList, enumerations]
  );

  // [DAP-017] Klik na dlaždici přepne (toggle) hodnotu stavu v multi-výběru sloupce
  // developmentState. Více dlaždic tak může být aktivních zároveň (OR uvnitř sloupce).
  // Sdílíme stejný stav se sloupcovým filtrem developmentState (MRT multi-select),
  // proto hodnota tohoto filtru je vždy pole. Prázdné pole = filtr se odebere.
  const handleOnCardClick = (developmentStateValue: string) => {
    const otherFilters = projectTableChanges.columnFilters.filter(
      (filter) => filter.id !== 'developmentState'
    );
    const existing = projectTableChanges.columnFilters.find(
      (filter) => filter.id === 'developmentState'
    );
    // Normalizace na pole — z URL / staršího stavu může přijít i skalár.
    const currentValues: string[] = Array.isArray(existing?.value)
      ? (existing?.value as string[])
      : existing?.value != null
        ? [String(existing?.value)]
        : [];

    const nextValues = currentValues.includes(developmentStateValue)
      ? currentValues.filter((value) => value !== developmentStateValue)
      : [...currentValues, developmentStateValue];

    const updatedColumnFilters = [...otherFilters];
    if (nextValues.length > 0) {
      updatedColumnFilters.push({ id: 'developmentState', value: nextValues });
    }

    updateTableChanges({
      ...projectTableChanges,
      columnFilters: updatedColumnFilters
    });
  };

  const downloadFile = (data: BlobPart, filename: string) => {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    setLoading(false);
  };

  const handleExportRowsExcel = (
    rowData: any[],
    exportMode: ExportMode,
    visibleColumnIds: string[]
  ) => {
    setLoading(true);
    let projectIds;
    if (ExportMode.ALL_ON_PAGE === exportMode) {
      projectIds = projectsTableData.data.map((project) => project.id);
    } else {
      projectIds = rowData.map((row) => row.original.id);
    }
    const projectsDataParameter = getProjectExportParameter(
      projectIds,
      ExportMode.ALL === exportMode,
      visibleColumnIds,
      projectTableChanges,
      filterMyProjects
    );
    useProjectsApi()
      .getProjectsExcelExport(projectsDataParameter, { responseType: 'arraybuffer' })
      .then((result) => {
        const { data, headers } = result;
        const contentDisposition = headers['content-disposition'];
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        const filename =
          matches && matches[1] ? matches[1].replace(/['"]/g, '') : 'export_projektu.xlsx';

        downloadFile(data, filename);
        loadExportSettings();
      })
      .catch((error) => {
        handleErrors(error);
        setLoading(false);
      });
  };

  const handleExportRowsCsv = async (
    rowData: any[],
    exportMode: ExportMode,
    visibleColumnIds: string[]
  ) => {
    setLoading(true);
    let projectIds;
    if (ExportMode.ALL_ON_PAGE === exportMode) {
      projectIds = projectsTableData.data.map((project) => project.id);
    } else {
      projectIds = rowData.map((row) => row.original.id);
    }
    const projectsDataParameter = getProjectExportParameter(
      projectIds,
      ExportMode.ALL === exportMode,
      visibleColumnIds,
      projectTableChanges,
      filterMyProjects
    );
    useProjectsApi()
      .getProjectsCSVExport(projectsDataParameter, { responseType: 'blob' })
      .then((result) => {
        const blob = new Blob([result.data], { type: 'text/csv' });

        // Create a link element and trigger the download
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'export_projektu.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setLoading(false);
        loadExportSettings();
      })
      .catch((error) => {
        handleErrors(error);
        setLoading(false);
      });
  };

  const handleExportLastExcel = (rowData: any[]) => {
    setLoading(true);
    const projectIds = rowData.map((row) => row.original.id);
    const repeatExportRequest = {
      projectIds: projectIds,
      exportType: RepeatExportRequestDtoExportTypeEnum.Excel
    };
    useProjectsApi()
      .repeatLastExport(repeatExportRequest, { responseType: 'arraybuffer' })
      .then((result) => {
        const { data, headers } = result;
        const contentDisposition = headers['content-disposition'];
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        const filename =
          matches && matches[1] ? matches[1].replace(/['"]/g, '') : 'export_projektu.xlsx';

        downloadFile(data, filename);
        loadExportSettings();
      })
      .catch((error) => {
        handleErrors(error);
        setLoading(false);
      });
  };

  const handleExportLastCsv = (rowData: any[]) => {
    setLoading(true);
    const projectIds = rowData.map((row) => row.original.id);
    const repeatExportRequest = {
      projectIds: projectIds,
      exportType: RepeatExportRequestDtoExportTypeEnum.Csv
    };
    useProjectsApi()
      .repeatLastExport(repeatExportRequest, { responseType: 'blob' })
      .then((result) => {
        const blob = new Blob([result.data], { type: 'text/csv' });

        // Create a link element and trigger the download
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'export_projektu.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setLoading(false);
        loadExportSettings();
      })
      .catch((error) => {
        handleErrors(error);
        setLoading(false);
      });
  };

  const deleteProjects = (rowData: any[]) => {
    setLoading(true);
    const projectIds = rowData.map((row) => row.original.id);
    useProjectsApi()
      .deleteProjects({ ids: projectIds })
      .then((result) => {
        callGetProjectsData();
        setLoading(false);
      })
      .catch((error) => {
        handleErrors(error, 'Při mazání vybraných projektů došlo k problému', false);
        setLoading(false);
      });
  };

  const openDeleteProjectsConfirmationModal = (rowData: any[]) => {
    modals.openConfirmModal({
      title: 'Opravdu chcete smazat vybrané projekty?',
      centered: true,
      children: <Text size="sm">Tuto akci nelze vrátit zpět.</Text>,
      labels: { confirm: 'Smazat', cancel: 'Zrušit' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteProjects(rowData)
    });
  };

  const handleDeleteProjects = (rowData: any[]) => {
    if (!canModify) return;
    openDeleteProjectsConfirmationModal(rowData);
  };

  const clearFilters = () => {
    updateTableChanges(defaultProjectTableChanges);
  };

  return (
    <>
      <ProjectsOverview
        onCardClick={handleOnCardClick}
        activeFilters={(() => {
          // [DAP-017] Aktivní dlaždice odpovídají hodnotám v multi-výběru developmentState.
          // Normalizujeme na pole (skalár z URL/staršího stavu → jednoprvkové pole).
          const value = projectTableChanges.columnFilters.find(
            (filter) => filter.id === 'developmentState'
          )?.value;
          if (Array.isArray(value)) {
            return value.map((item) => String(item));
          }
          return value != null ? [String(value)] : [];
        })()}
        filterMyProjects={filterMyProjects}
      />
      <Space h="md" />
      <MaterialTable
        columns={columns}
        tableData={projectsTableData}
        isLoading={loading || !columnSettingsLoaded}
        canModify={canModify}
        handleExportRowsExcel={handleExportRowsExcel}
        handleExportRowsCsv={handleExportRowsCsv}
        handleExportLastExcel={handleExportLastExcel}
        handleExportLastCsv={handleExportLastCsv}
        hasExportSettings={hasExportSettings}
        deleteProjects={handleDeleteProjects}
        clearFilters={clearFilters}
        externalColumnFilters={projectTableChanges.columnFilters}
        initialSorting={projectTableChanges.sorting}
        initialPagination={projectTableChanges.pagination}
        initialGlobalFilter={projectTableChanges.globalFilter}
        initialColumnVisibility={columnVisibility}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        initialColumnOrder={columnOrder}
        onColumnOrderChange={handleColumnOrderChange}
        onTableChange={(event) => {
          updateTableChanges({
            ...projectTableChanges,
            columnFilters: event.columnFilters,
            pagination: event.pagination,
            sorting: event.sorting,
            globalFilter: event.globalFilter
          });
        }}
      />
    </>
  );
};
